// src/features/boe/utils/studentEnrollmentUtils.js

import React from "react";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";
import { sumSectionTotals } from "../../../budget/utils/programBudgetUtils.js";

export function buildBudgetTotalsByYear(budgetProgramDocs) {
  const docs = budgetProgramDocs || {};
  const out = new Map(); // year -> { approvedSum, proposedSum, actualSum, sawApproved, sawProposed, sawActual }

  Object.values(docs).forEach((docData) => {
    if (!docData || typeof docData !== "object") return;

    Object.keys(docData).forEach((yKey) => {
      const y = Number(yKey);
      if (!Number.isFinite(y)) return;

      const yearObj = docData?.[yKey];
      if (!yearObj || typeof yearObj !== "object") return;

      if (!out.has(y)) {
        out.set(y, {
          approvedSum: 0,
          proposedSum: 0,
          actualSum: 0,
          sawApproved: false,
          sawProposed: false,
          sawActual: false,
        });
      }

      const slot = out.get(y);

      const a = sumSectionTotals(yearObj?.Approved);
      if (typeof a === "number" && Number.isFinite(a)) {
        slot.approvedSum += a;
        slot.sawApproved = true;
      }

      const p = sumSectionTotals(yearObj?.Proposed);
      if (typeof p === "number" && Number.isFinite(p)) {
        slot.proposedSum += p;
        slot.sawProposed = true;
      }

      const act = sumSectionTotals(yearObj?.Actual);
      if (typeof act === "number" && Number.isFinite(act)) {
        slot.actualSum += act;
        slot.sawActual = true;
      }
    });
  });

  return out;
}

export function buildDistrictCostPerStudentSeries({
  docData,
  yearNumbers,
  budgetTotalsByYear,
  kind, // "approved" | "actual"
}) {
  if (!docData?.years) return { labels: [], series: [], groups: [] };

  const enrollmentYears = (yearNumbers || []).filter(Boolean);

  const years = getEligibleYears({
    enrollmentYears,
    budgetTotalsByYear,
    kind,
    getDistrictEnrollmentForYear: (yKey) =>
      getDistrictEnrollmentForYearFromDoc(docData, yKey),
  });

  if (!years.length) return { labels: [], series: [], groups: [] };

  const labels = years.map((y) => formatFiscalYearFromStart(y));

  if (kind === "approved") {
    const approved = years.map((y) => {
      const slot = budgetTotalsByYear.get(y);
      const denom = getDistrictEnrollmentForYearFromDoc(docData, String(y));
      if (!slot || !slot.sawApproved) return null;
      if (!hasPositiveNumber(denom)) return null;
      return slot.approvedSum / denom;
    });

    const proposedOnly = years.map((y) => {
      const slot = budgetTotalsByYear.get(y);
      const denom = getDistrictEnrollmentForYearFromDoc(docData, String(y));
      if (!slot || slot.sawApproved || !slot.sawProposed) return null;
      if (!hasPositiveNumber(denom)) return null;
      return slot.proposedSum / denom;
    });

    return {
      labels,
      series: [
        {
          key: "district_approved_per_student",
          label: "District – Budgeted $/Student",
          data: approved,
          proposedData: proposedOnly,
          group: "district",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
      groups: [
        {
          key: "group_district",
          label: "District",
          groupKey: "district",
          seriesKeys: ["district_approved_per_student"],
          defaultVisible: true,
        },
      ],
    };
  }

  // kind === "actual"
  const actual = years.map((y) => {
    const slot = budgetTotalsByYear.get(y);
    const denom = getDistrictEnrollmentForYearFromDoc(docData, String(y));
    if (!slot || !slot.sawActual) return null;
    if (!hasPositiveNumber(denom)) return null;
    return slot.actualSum / denom;
  });

  return {
    labels,
    series: [
      {
        key: "district_actual_per_student",
        label: "District – Actual $/Student",
        data: actual,
        group: "district",
        defaultVisible: true,
        valueType: "currency",
      },
    ],
    groups: [
      {
        key: "group_district",
        label: "District",
        groupKey: "district",
        seriesKeys: ["district_actual_per_student"],
        defaultVisible: true,
      },
    ],
  };
}

export function buildCostPerStudentRowsForInflation({
  docData,
  yearNumbers,
  budgetTotalsByYear,
  kind, // "approved" | "proposedOnly" | "actual"
}) {
  if (!docData?.years) return [];

  const years = (yearNumbers || []).filter(Boolean);

  return years
    .map((y) => {
      const slot = budgetTotalsByYear.get(y);
      const denom = getDistrictEnrollmentForYearFromDoc(docData, String(y));
      if (!slot) return null;
      if (!hasPositiveNumber(denom)) return null;

      if (kind === "approved") {
        if (!slot.sawApproved) return null;
        const v = slot.approvedSum / denom;
        return Number.isFinite(v) ? { year: y, label: "District", costPerStudent: v } : null;
      }

      if (kind === "proposedOnly") {
        if (slot.sawApproved || !slot.sawProposed) return null;
        const v = slot.proposedSum / denom;
        return Number.isFinite(v) ? { year: y, label: "District", costPerStudent: v } : null;
      }

      if (kind === "actual") {
        if (!slot.sawActual) return null;
        const v = slot.actualSum / denom;
        return Number.isFinite(v) ? { year: y, label: "District", costPerStudent: v } : null;
      }

      return null;
    })
    .filter(Boolean);
}

/**
 * Safely converts a Firestore "year" key (string) into a numeric year.
 */
export function toYearNumber(yearKey) {
  const n = Number(yearKey);
  if (!Number.isFinite(n)) return null;
  if (n < 2000 || n > 2100) return null;
  return n;
}

/**
 * Builds a two-line table header cell for a given fiscal year label.
 */
export function buildTwoLineHeader(fyLabel, subLabel = "Enrollment") {
  return (
    <div className="leading-tight text-center whitespace-nowrap">
      <div className="font-semibold">{fyLabel}</div>
      <div>{subLabel}</div>
    </div>
  );
}

/**
 * Adds "Change" and "% Change" fields to a row based on the last two columns
 * in the provided year metadata window.
 */
export function attachIncreaseFields(row, yearMeta) {
  if (!Array.isArray(yearMeta) || yearMeta.length < 2) return row;

  const prev = yearMeta[yearMeta.length - 2];
  const curr = yearMeta[yearMeta.length - 1];

  const prevVal = row[prev.colKey];
  const currVal = row[curr.colKey];

  if (typeof prevVal === "number" && typeof currVal === "number") {
    const diff = currVal - prevVal;
    row.increase = diff;
    row.percentIncrease = prevVal !== 0 ? diff / prevVal : null;
  } else {
    row.increase = null;
    row.percentIncrease = null;
  }

  return row;
}

/**
 * Computes the average class size from a Firestore `classes` object.
 */
export function computeAverageClassSize(classesObj) {
  if (!classesObj || typeof classesObj !== "object") return null;

  const values = [];

  for (const arr of Object.values(classesObj)) {
    if (!Array.isArray(arr)) continue;
    for (const v of arr) {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) {
        values.push(v);
      }
    }
  }

  if (!values.length) return null;

  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

export function formatAvgClassSize(val) {
  if (val == null || !Number.isFinite(val)) return "—";
  return Number(val).toFixed(1);
}

export function formatCount(val) {
  if (val == null || !Number.isFinite(val)) return "—";
  return val.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export function slugifyKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function gradeRank(key) {
  const k = String(key || "").toLowerCase();
  if (k === "prek") return 0;
  if (k === "k") return 1;
  const m = k.match(/^g(\d{1,2})$/);
  if (m) return 2 + Number(m[1]);
  return 999;
}

export function gradeLabel(key) {
  const k = String(key || "").toLowerCase();
  if (k === "prek") return "Pre-K";
  if (k === "k") return "Kindergarten";
  const m = k.match(/^g(\d{1,2})$/);
  if (m) return `Grade ${Number(m[1])}`;
  return String(key);
}

export function hasPositiveNumber(n) {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

/**
 * Choose which years should be plotted, based on data we can actually compute.
 * - approved: requires Approved OR Proposed (fallback) + district enrollment
 * - actual: requires Actual + district enrollment
 */
export function getEligibleYears({
  enrollmentYears,
  budgetTotalsByYear,
  kind, // "approved" | "actual"
  getDistrictEnrollmentForYear,
}) {
  return (enrollmentYears || [])
    .filter(Boolean)
    .filter((y) => budgetTotalsByYear?.has?.(y))
    .filter((y) => {
      const slot = budgetTotalsByYear.get(y);
      if (!slot) return false;

      const denom = getDistrictEnrollmentForYear(String(y));
      if (!hasPositiveNumber(denom)) return false;

      if (kind === "approved") return slot.sawApproved || slot.sawProposed;
      if (kind === "actual") return slot.sawActual;

      return false;
    })
    .sort((a, b) => a - b);
}

/**
 * Compute districts' enrollment for a year.
 * Prefers explicit `districtTotals.grandTotal`, otherwise computes the same
 * "grand total" used elsewhere (total + preK + outOfDistrict).
 */
export function getDistrictEnrollmentForYearFromDoc(docData, yKey) {
  const dt = docData?.years?.[yKey]?.districtTotals;
  if (!dt || typeof dt !== "object") return null;

  const direct =
    typeof dt.grandTotal === "number" && Number.isFinite(dt.grandTotal)
      ? dt.grandTotal
      : null;

  if (direct != null) return direct;

  const base = dt.total;
  const preK = dt.preK;
  const ood = dt.outOfDistrict;

  const ok =
    typeof base === "number" &&
    typeof preK === "number" &&
    typeof ood === "number" &&
    Number.isFinite(base) &&
    Number.isFinite(preK) &&
    Number.isFinite(ood);

  return ok ? base + preK + ood : null;
}

export function getSchoolEnrollmentForYearFromDoc(docData, yKey, schoolKey) {
  const v = docData?.years?.[yKey]?.schools?.[schoolKey]?.total;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Shared label builder used by the charts in this file.
 */
export function buildFiscalYearLabels(years) {
  return (years || []).map((y) => formatFiscalYearFromStart(Number(y)));
}

/**
 * Derive schools metadata (name/level) across all years.
 */
export function buildSchoolsMeta(docData) {
  const out = {};
  const yearsObj = docData?.years || {};
  for (const yKey of Object.keys(yearsObj)) {
    const yObj = yearsObj?.[yKey];
    const schools = yObj?.schools || {};
    for (const [schoolKey, s] of Object.entries(schools)) {
      if (!out[schoolKey]) {
        out[schoolKey] = {
          key: schoolKey,
          name: s?.name || schoolKey,
          level: s?.level || "unknown",
        };
      }
    }
  }
  return out;
}

export function sortSchoolKeysByLevelThenName(schoolsMeta) {
  const levelOrder = { elementary: 1, middle: 2, secondary: 3, unknown: 9 };
  return Object.keys(schoolsMeta || {}).sort((a, b) => {
    const A = schoolsMeta?.[a];
    const B = schoolsMeta?.[b];
    const la = levelOrder[A?.level] ?? 9;
    const lb = levelOrder[B?.level] ?? 9;
    if (la !== lb) return la - lb;
    return String(A?.name || a).localeCompare(String(B?.name || b));
  });
}

export function sectionForLevel(level) {
  if (level === "elementary") return "Elementary Schools";
  if (level === "middle") return "Middle School";
  if (level === "secondary") return "High School";
  return "Schools";
}

/**
 * Pie items for enrollment breakdown for a selected year.
 * (Kept pure so the component only passes docData + schoolsMeta + pieYear)
 */
export function buildEnrollmentBreakdownPieItems({ docData, schoolsMeta, pieYear }) {
  if (!docData?.years || !pieYear) return [];

  const yKey = String(pieYear);
  const yObj = docData.years?.[yKey];
  if (!yObj) return [];

  const schools = yObj?.schools || {};
  const dt = yObj?.districtTotals || {};

  let elementarySum = 0;
  let middleSum = 0;
  let highSum = 0;

  for (const schoolKey of Object.keys(schools)) {
    const level =
      schoolsMeta?.[schoolKey]?.level ||
      schools?.[schoolKey]?.level ||
      "unknown";

    const v = schools?.[schoolKey]?.total;
    if (typeof v !== "number" || !Number.isFinite(v)) continue;

    if (level === "elementary") elementarySum += v;
    else if (level === "middle") middleSum += v;
    else if (level === "secondary") highSum += v;
  }

  const preK = typeof dt?.preK === "number" && Number.isFinite(dt.preK) ? dt.preK : 0;
  const outOfDistrict =
    typeof dt?.outOfDistrict === "number" && Number.isFinite(dt.outOfDistrict)
      ? dt.outOfDistrict
      : 0;

  const alt =
    typeof dt?.ghsAlternateProgram === "number" && Number.isFinite(dt.ghsAlternateProgram)
      ? dt.ghsAlternateProgram
      : 0;

  const highWithAlt = highSum + alt;

  return [
    { key: "students_elementary", label: "Elementary", value: elementarySum, valueType: "count", group: "other" },
    { key: "students_middle", label: "Middle School", value: middleSum, valueType: "count", group: "other" },
    { key: "students_high", label: "High School", value: highWithAlt, valueType: "count", group: "other" },
    { key: "students_prek", label: "Pre-K", value: preK, valueType: "count", group: "other" },
    { key: "students_out_of_district", label: "Out of District", value: outOfDistrict, valueType: "count", group: "other" },
  ];
}

export const COST_PER_STUDENT_FIELD_DEFS = [
  {
    key: "costPerStudent",
    label: "Cost per Student",
    valueType: "currency",
    group: "other",
    defaultVisible: true,
    sources: [],
  },
];
